import React from "react";
import { Box, Flex, Grid, GridItem, Text, Tooltip } from "@chakra-ui/react";
import CustomIcon, { AssetTypes } from "../../../../../../../public/icons-v1_1";
import {
  ORDER_HISTORY_ATTACHMENTS_FOLDERS,
  OrderHistoryAttachmentsFolder,
} from "@/features/my-order/features/order-history/constants";
import { AttachmentInvoiceTypes } from "@/shared/types";
import useOrderHistoryDetail from "@/features/my-order/features/order-history/hooks/useOrderHistoryDetail";
import { OrderHistoryGetDetailResponse } from "@/features/my-order/features/order-history/types";
import { formatDateToYMDEGeneral } from "@/shared/utils";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import icons from "../../../../../../../public/icons-v1_1/icons";

function OrderHistoryDocumentFileSelector() {
  const { data } = useOrderHistoryDetail();
  const [currentFolderTypes, setCurrentFolderTypes] = React.useState<
    AttachmentInvoiceTypes[]
  >(ORDER_HISTORY_ATTACHMENTS_FOLDERS[0].type);
  const currentFileList = data?.attachments.filter((att) =>
    currentFolderTypes.includes(att.type)
  );
  const { tTitle, i18n, tSentence } = useI18nHelper();
  const TEMPLATE_COLUMNS = `${i18n.language === "ko" ? "200px" : "250px"} 1fr`;

  return (
    <Grid
      boxShadow={"0 0 0 0.5px #8F8F8C"}
      borderRadius={"6px"}
      overflow={"hidden"}
      templateColumns={TEMPLATE_COLUMNS}
      templateRows={"1fr 220px"}
    >
      <GridItem colSpan={2} background={"#F7F9FA"} padding={"8px 12px"}>
        <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
          {tTitle("order_history:attachments")} {data?.attachments.length ?? 0}
          {i18n.language === "ko" ? "개" : ""}
        </Text>
      </GridItem>
      <GridItem>
        <Box overflowY={"scroll"} height={"100%"}>
          {ORDER_HISTORY_ATTACHMENTS_FOLDERS.map((folder, i) => {
            const count =
              data?.attachments?.filter((att) => folder.type.includes(att.type))
                .length ?? 0;

            return (
              <FolderItem
                key={i}
                folder={folder}
                count={count}
                onClick={() => setCurrentFolderTypes(folder.type)}
                currentTypes={currentFolderTypes}
              />
            );
          })}
        </Box>
      </GridItem>
      <GridItem>
        <Box overflow={"auto"} height={"100%"}>
          {currentFileList?.length === 0 && (
            <Flex
              justifyContent={"center"}
              alignItems={"center"}
              height={"100%"}
            >
              <Text textStyle={"body-md-regular"} color={"#444444"}>
                {tSentence("order_history:no_result_msg.file")}
              </Text>
            </Flex>
          )}
          {currentFileList?.map((file, i) => (
            <FileItem file={file} key={i} />
          ))}
        </Box>
      </GridItem>
    </Grid>
  );
}

export default OrderHistoryDocumentFileSelector;

interface DocumentFolderItemProps {
  folder: OrderHistoryAttachmentsFolder;
  count: number;
  onClick?: () => void;
  currentTypes: AttachmentInvoiceTypes[];
}

const FolderItem = ({
  folder,
  count,
  onClick,
  currentTypes,
}: DocumentFolderItemProps) => {
  const style = {
    "--icon-primary": "#005AAD",
    "--icon-secondary": "#1272EF",
  } as React.CSSProperties;

  const currentSet = React.useMemo(() => new Set(currentTypes), [currentTypes]);
  const isSelected =
    folder.type.length === currentTypes.length &&
    folder.type.every((t) => currentSet.has(t));
  const { t } = useI18nHelper();

  return (
    <Flex
      paddingX={"12px"}
      gap={"4px"}
      height={"40px"}
      alignItems={"center"}
      cursor={"pointer"}
      _hover={{
        backgroundColor: "#F7F9FA",
      }}
      _active={{
        backgroundColor: "#F0F0F0",
      }}
      onClick={onClick}
      background={isSelected ? "#F0F0F0" : "inherit"}
    >
      <CustomIcon src={"folder-solid"} width={24} height={24} style={style} />
      <Flex justifyContent={"space-between"} flex={1} alignItems={"center"}>
        <Text textStyle={"body-sm-medium"} color={"#444444"}>
          {t("order_history:" + folder.name)}
        </Text>
        <Flex gap={"2px"} alignItems={"center"}>
          <Text textStyle={"body-sm-medium"} color={"#444444"}>
            {count}
          </Text>
          <CustomIcon
            src={"chevron-right"}
            width={16}
            height={16}
            color={"#8F8F8C"}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

const FileItem = ({
  file,
}: {
  file: OrderHistoryGetDetailResponse["attachments"][number];
}) => {
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const { tTitle, i18n, t } = useI18nHelper();
  const extension = file.name.split(".").pop();
  const fileExt = extension === "xlsx" ? "xls" : extension;

  // 아이콘 존재 여부를 체크하는 타입 가드 함수
  const isAssetType = (src: string): src is AssetTypes => {
    return src in icons;
  };

  // 컴포넌트 내부 사용
  const iconSrc = `file-${fileExt}`;
  const finalSrc: AssetTypes = isAssetType(iconSrc) ? iconSrc : "file"; // 아이콘이 없으면 기본 file 아이콘 사용

  return (
    <Flex
      justifyContent={"space-between"}
      alignItems={"center"}
      // _hover={{
      //   backgroundColor: "#F7F9FA",
      // }}
      // cursor={"pointer"}
    >
      <Flex gap={"4px"} alignItems={"center"} paddingX={"12px"} height={"40px"}>
        <CustomIcon src={finalSrc} width={24} height={24} />
        <Tooltip label={file.name}>
          <Text
            textStyle={"body-sm-medium"}
            color={"#444444"}
            maxWidth={"323px"}
            noOfLines={1}
          >
            {file.name}
          </Text>
        </Tooltip>
      </Flex>
      <Flex gap={"8px"} alignItems={"center"} paddingX={"12px"}>
        <Text
          textStyle={"caption-regular"}
          color={"#6F6F6C"}
          whiteSpace={"nowrap"}
        >
          {formatDateToYMDEGeneral(
            file?.created_at ?? "",
            i18n.language === "ko",
            "-"
          )}{" "}
          {t("order_history:uploaded")}
        </Text>
        <Tooltip label={tTitle("download")}>
          <Box
            _hover={{
              color: "#1272EF",
            }}
            _active={{
              color: "#00417D",
            }}
          >
            <CustomIcon
              src={"download"}
              width={20}
              height={20}
              onClick={() => fireDownloadUnoFile({ unoFileId: file.id }, {})}
            />
          </Box>
        </Tooltip>
      </Flex>
    </Flex>
  );
};
